import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Activity, Loader2, MapPin, Stethoscope, Globe, GraduationCap, Briefcase } from 'lucide-react';
import { COUNTRIES, PROVINCES } from '../data/countries';

const OnboardingPage = () => {
  const [specialty, setSpecialty] = useState('');
  const [role, setRole] = useState('');
  const [professionType, setProfessionType] = useState('');
  const [country, setCountry] = useState('');
  const [province, setProvince] = useState('');
  const [loading, setLoading] = useState(false);
  const { updateUser } = useAuth();
  const navigate = useNavigate();

  const provinces = PROVINCES[country] || PROVINCES.DEFAULT;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!specialty || !role || !professionType || !country) return;

    setLoading(true);
    try {
      await api.completeOnboarding({ 
        specialty, 
        role, 
        profession_type: professionType,
        country, 
        province: province || 'Not specified'
      });
      updateUser({ 
        specialty, 
        role, 
        profession_type: professionType,
        country, 
        province: province || 'Not specified', 
        onboarding_complete: true 
      });
      navigate('/');
    } catch (err) {
      console.error('Onboarding error:', err);
    } finally {
      setLoading(false);
    }
  };

  const isFormValid = specialty && role && professionType && country;

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-b from-slate-50 to-white">
      <Card className="w-full max-w-lg shadow-lg border-slate-200" data-testid="onboarding-card">
        <CardHeader className="text-center pb-2">
          <div className="flex justify-center mb-4">
            <div className="w-12 h-12 rounded-xl bg-slate-900 flex items-center justify-center">
              <Activity className="h-7 w-7 text-white" />
            </div>
          </div>
          <CardTitle className="text-2xl font-semibold text-slate-900">Complete Your Profile</CardTitle>
          <CardDescription className="text-slate-500">
            Help us personalize your evidence search with regional guidelines
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Privacy Notice */}
          <div className="mb-6 p-3 bg-slate-50 rounded-lg border border-slate-100">
            <p className="text-xs text-slate-600 leading-relaxed">
              <strong>Privacy:</strong> This information is only used to tailor search results and improve context relevance. 
              All analytics are performed on de-identified, aggregate data only.
            </p>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Specialty - Open Text */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Stethoscope className="h-4 w-4 text-slate-500" />
                Specialty / Area of Practice <span className="text-red-500">*</span>
              </Label>
              <Input
                value={specialty}
                onChange={(e) => setSpecialty(e.target.value)}
                placeholder="e.g., Infectious Disease, Emergency Medicine, Nursing..."
                className="rounded-lg"
                required
                data-testid="specialty-input"
              />
            </div>

            {/* Role - Trainee or Faculty/Staff */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <GraduationCap className="h-4 w-4 text-slate-500" />
                Role <span className="text-red-500">*</span>
              </Label>
              <Select value={role} onValueChange={setRole} required>
                <SelectTrigger data-testid="role-select">
                  <SelectValue placeholder="Select your role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="trainee">Trainee (Student, Resident, Fellow)</SelectItem>
                  <SelectItem value="faculty_staff">Faculty / Staff</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Profession Type */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Briefcase className="h-4 w-4 text-slate-500" />
                Profession Type <span className="text-red-500">*</span>
              </Label>
              <Select value={professionType} onValueChange={setProfessionType} required>
                <SelectTrigger data-testid="profession-type-select">
                  <SelectValue placeholder="Select profession type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="medical_doctor">Medical Doctor (MD, DO)</SelectItem>
                  <SelectItem value="allied_health">Allied Health Professional</SelectItem>
                  <SelectItem value="non_md_researcher">Non-MD Researcher</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-400">
                Allied Health includes Nursing, Pharmacy, Physiotherapy, etc.
              </p>
            </div>

            {/* Country */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Globe className="h-4 w-4 text-slate-500" />
                Country <span className="text-red-500">*</span>
              </Label>
              <Select value={country} onValueChange={(v) => { setCountry(v); setProvince(''); }}>
                <SelectTrigger data-testid="country-select">
                  <SelectValue placeholder="Select your country" />
                </SelectTrigger>
                <SelectContent>
                  {COUNTRIES.map((c) => (
                    <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Province/State */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-slate-500" />
                Province / State <span className="text-slate-400 text-xs font-normal">(optional)</span>
              </Label>
              <Select value={province} onValueChange={setProvince} disabled={!country}>
                <SelectTrigger data-testid="province-select">
                  <SelectValue placeholder="Select your region (optional)" />
                </SelectTrigger>
                <SelectContent>
                  {provinces.map((p) => (
                    <SelectItem key={p} value={p}>{p}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-400">
                Helps prioritize local clinical guidelines (e.g., BCCDC for BC)
              </p>
            </div>

            <Button
              type="submit"
              disabled={loading || !isFormValid}
              className="w-full h-11 rounded-full bg-slate-900 hover:bg-slate-800 text-white font-medium mt-2"
              data-testid="onboarding-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Continue to Search'
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default OnboardingPage;
